import { createTheme } from '@mui/material/styles';

// Beacon design system — ported from Design Files (dashboard-shared.css / design system.md).
// Ink-violet primary, lime/pink accents, hairline borders, flat surfaces.
const designSystem = {
  colors: {
    primary: {
      main: '#150f23',
      light: '#2a1f47',
      dark: '#0e0a19',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#6a5fc1',
      light: '#79628c',
      dark: '#422082',
      contrastText: '#ffffff',
    },
    tertiary: {
      main: '#c2ef4e',
      light: '#d5f581',
      dark: '#4a5a12',
      contrastText: '#1f1633',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
      section: '#f0f0f0',
      nav: '#ffffff',
    },
    text: {
      primary: '#1f1633',
      secondary: '#4a4257',
      disabled: '#a6a0b3',
    },
    error: {
      main: '#b42318',
      light: '#fdece9',
      dark: '#912016',
      contrastText: '#ffffff',
    },
    warning: {
      main: '#c2410c',
      light: '#fdf0e7',
      dark: '#9a2d08',
      contrastText: '#ffffff',
    },
    success: {
      main: '#15803d',
      light: '#e8f5ec',
      dark: '#106b33',
      contrastText: '#ffffff',
    },
    hairline: {
      cloud: '#e5e7eb',
      cool: '#cfcfdb',
    },
  },
  typography: {
    fontFamily: '"Rubik", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    headlineFont: '"Space Grotesk", "Rubik", sans-serif',
  },
  borderRadius: {
    none: '0px',
    sm: '6px',
    md: '8px',
    lg: '10px',
    xl: '12px',
    full: '9999px',
  },
  shadows: {
    floating: '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
    ghost: 'inset 0 0 0 1px #e5e7eb',
  },
};

const defaultThemeConfig = {
  palette: {
    primary: designSystem.colors.primary,
    secondary: designSystem.colors.secondary,
    error: designSystem.colors.error,
    warning: designSystem.colors.warning,
    success: designSystem.colors.success,
    background: {
      default: designSystem.colors.background.default,
      paper: designSystem.colors.background.paper,
    },
    text: designSystem.colors.text,
    divider: designSystem.colors.hairline.cloud,
  },
  typography: {
    fontFamily: designSystem.typography.fontFamily,
    h1: { fontFamily: designSystem.typography.headlineFont, fontWeight: 600, letterSpacing: '-0.01em' },
    h2: { fontFamily: designSystem.typography.headlineFont, fontWeight: 600, letterSpacing: '-0.01em' },
    h3: { fontFamily: designSystem.typography.headlineFont, fontWeight: 600, letterSpacing: '-0.01em' },
    h4: { fontFamily: designSystem.typography.headlineFont, fontWeight: 600, letterSpacing: '-0.01em' },
    h5: { fontFamily: designSystem.typography.headlineFont, fontWeight: 600 },
    h6: { fontFamily: designSystem.typography.headlineFont, fontWeight: 600 },
    subtitle1: { fontFamily: designSystem.typography.fontFamily, fontWeight: 500 },
    subtitle2: { fontFamily: designSystem.typography.fontFamily, fontWeight: 500, color: designSystem.colors.text.secondary },
    button: { fontFamily: designSystem.typography.fontFamily, fontWeight: 600, textTransform: 'none', letterSpacing: '0' },
    overline: { fontFamily: designSystem.typography.fontFamily, fontWeight: 600, letterSpacing: '0.1em' },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: designSystem.borderRadius.md,
          minHeight: '38px',
          padding: '0 15px',
          fontSize: '13px',
          fontWeight: 600,
          textTransform: 'none',
        },
        sizeLarge: {
          minHeight: '46px',
          padding: '0 22px',
          fontSize: '13.5px',
        },
        sizeSmall: {
          minHeight: '30px',
          padding: '0 12px',
          fontSize: '12px',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: '#150f23',
          color: '#ffffff',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.2px',
          '&:hover': {
            backgroundColor: '#0e0a19',
          },
        },
        outlined: {
          borderColor: designSystem.colors.hairline.cloud,
          backgroundColor: '#ffffff',
          color: designSystem.colors.text.primary,
          '&:hover': {
            backgroundColor: '#f0f0f0',
            borderColor: designSystem.colors.hairline.cloud,
          },
        },
        text: {
          color: designSystem.colors.secondary.main,
          '&:hover': {
            backgroundColor: 'rgba(106,95,193,0.08)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: designSystem.borderRadius.xl,
          border: `1px solid ${designSystem.colors.hairline.cloud}`,
          boxShadow: 'none',
          backgroundColor: designSystem.colors.background.paper,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: designSystem.borderRadius.lg,
          border: 'none',
          boxShadow: 'none',
        },
        outlined: {
          border: `1px solid ${designSystem.colors.hairline.cloud}`,
        },
        elevation1: {
          boxShadow: 'none',
          border: `1px solid ${designSystem.colors.hairline.cloud}`,
        },
        elevation2: {
          boxShadow: designSystem.shadows.floating,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: designSystem.borderRadius.sm,
            backgroundColor: '#ffffff',
            '& fieldset': {
              borderColor: designSystem.colors.hairline.cool,
            },
            '&:hover fieldset': {
              borderColor: designSystem.colors.text.disabled,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: designSystem.borderRadius.full,
          fontWeight: 600,
          fontSize: '12px',
        },
        filledInfo: {
          backgroundColor: 'rgba(21,15,35,0.08)',
          color: '#150f23',
        },
        filledSuccess: {
          backgroundColor: 'rgba(21,128,61,0.10)',
          color: '#106b33',
        },
        filledWarning: {
          backgroundColor: 'rgba(194,65,12,0.10)',
          color: '#9a2d08',
        },
        filledError: {
          backgroundColor: 'rgba(180,35,24,0.08)',
          color: '#912016',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(14px)',
          color: designSystem.colors.text.primary,
          boxShadow: 'none',
          borderBottom: `1px solid ${designSystem.colors.hairline.cloud}`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${designSystem.colors.hairline.cloud}`,
          padding: '12px 16px',
        },
        head: {
          fontWeight: 600,
          color: '#7b7389',
          backgroundColor: 'transparent',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          fontSize: '0.7rem',
          borderBottom: `1px solid ${designSystem.colors.hairline.cloud}`,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          '&:hover': {
            backgroundColor: '#f7f7fa',
          },
          '&:last-child td': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: designSystem.borderRadius.xl,
          border: `1px solid ${designSystem.colors.hairline.cloud}`,
          boxShadow: designSystem.shadows.floating,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: designSystem.borderRadius.lg,
          border: `1px solid ${designSystem.colors.hairline.cloud}`,
          boxShadow: designSystem.shadows.floating,
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '32px',
          paddingRight: '32px',
          '@media (min-width: 600px)': {
            paddingLeft: '40px',
            paddingRight: '40px',
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: designSystem.colors.background.default,
          color: designSystem.colors.text.primary,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
  },
};

// Resolve the best primary/secondary values from any config shape the backend may send.
function resolvePrimary(config) {
  return (
    config.theme?.primary ||
    config.palette?.primary?.main ||
    config.primaryColor ||
    designSystem.colors.primary.main
  );
}

function resolveSecondary(config) {
  return (
    config.theme?.secondary ||
    config.palette?.secondary?.main ||
    config.secondaryColor ||
    designSystem.colors.secondary.main
  );
}

function hexToRgba(hex, alpha) {
  const n = parseInt(hex.replace('#', ''), 16);
  return `rgba(${(n >> 16) & 0xff}, ${(n >> 8) & 0xff}, ${n & 0xff}, ${alpha})`;
}

function adjustHex(hex, delta) {
  const n = parseInt(hex.replace('#', ''), 16);
  const clamp = (v) => Math.max(0, Math.min(255, v + delta));
  const r = clamp((n >> 16) & 0xff);
  const g = clamp((n >> 8) & 0xff);
  const b = clamp(n & 0xff);
  return `#${[r, g, b].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

export function createAppTheme(config = {}) {
  // Tenant colors no longer drive UI chrome — the Beacon ink primary is fixed
  // (demo spec: near-black primary regardless of brand). The tenant's brand
  // color is exposed as palette.tenant for logos/decorative accents only.
  const tenantMain = resolvePrimary(config);
  const tenantSecondary = resolveSecondary(config);
  const primaryMain = designSystem.colors.primary.main;

  const mergedPalette = {
    ...defaultThemeConfig.palette,
    tenant: { main: tenantMain, secondary: tenantSecondary, contrastText: '#ffffff' },
  };

  const components = {
    ...defaultThemeConfig.components,
    MuiButton: {
      ...defaultThemeConfig.components.MuiButton,
      styleOverrides: {
        ...defaultThemeConfig.components.MuiButton.styleOverrides,
        containedPrimary: {
          backgroundColor: primaryMain,
          color: '#ffffff',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.2px',
          '&:hover': {
            backgroundColor: adjustHex(primaryMain, -14),
          },
        },
      },
    },
    MuiTextField: {
      ...defaultThemeConfig.components.MuiTextField,
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: designSystem.borderRadius.sm,
            backgroundColor: '#ffffff',
            '& fieldset': {
              borderColor: designSystem.colors.hairline.cool,
            },
            '&:hover fieldset': {
              borderColor: designSystem.colors.text.disabled,
            },
            '&.Mui-focused': {
              '& fieldset': {
                borderColor: hexToRgba(primaryMain, 0.5),
                borderWidth: '2px',
              },
            },
          },
        },
      },
    },
  };

  return createTheme({
    palette: mergedPalette,
    typography: { ...defaultThemeConfig.typography, ...(config.typography || {}) },
    shape: defaultThemeConfig.shape,
    components,
  });
}

export default createAppTheme();
export { designSystem };
